class FrequentlyBoughtTogether extends HTMLElement {
  #abortController = null;
  #observer = null;

  connectedCallback() {
    this.#observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          this.#observer.disconnect();
          this.#observer = null;
          this.#fetchRecommendations();
        }
      },
      { rootMargin: '200px' }
    );
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#observer?.disconnect();
  }

  async #fetchRecommendations() {
    const {
      variantId,
      productId,
      productType,
      limit,
      algoliaAppId: appId,
      algoliaApiKey: apiKey,
      algoliaIndex: indexName,
    } = this.dataset;

    if (!productId || !appId || !apiKey || !indexName) return;

    const headers = {
      'X-Algolia-Application-Id': appId,
      'X-Algolia-API-Key': apiKey,
      'Content-Type': 'application/json',
    };

    // Use objectID from data attribute if available, otherwise resolve via Search
    const objectID = this.dataset.algoliaObjectId
      || await this.#resolveObjectID(appId, apiKey, indexName, productId, variantId, headers);
    if (!objectID) {
      this.#hide('no-objectid');
      return;
    }

    const url = `https://${appId}-dsn.algolia.net/1/indexes/*/recommendations`;
    const maxItems = parseInt(limit, 10) || 3;

    this.#abortController?.abort();
    this.#abortController = new AbortController();

    const buildRequest = (withTypeFilter) => {
      const baseFilters = 'inventory_available:true';
      const filters = (withTypeFilter && productType)
        ? `${baseFilters} AND product_type:"${productType.replace(/"/g, '\\"')}"`
        : baseFilters;
      return {
        indexName,
        objectID,
        model: 'related-products',
        maxRecommendations: maxItems,
        threshold: 0,
        queryParameters: {
          filters,
          attributesToRetrieve: [
            'objectID', 'title', 'handle', 'price',
            'compare_at_price', 'image', 'id',
          ],
        },
      };
    };

    try {
      const cache = window.__algoliaRecommendCache;
      const cacheKey = ['fbt', objectID, productType || ''];
      const cached = cache?.get(cacheKey);

      let hits;
      if (cached) {
        hits = cached;
      } else {
        // Batch both requests (with and without type filter) in a single POST
        const requests = productType
          ? [buildRequest(true), buildRequest(false)]
          : [buildRequest(false)];

        const res = await fetch(url, {
          method: 'POST',
          signal: this.#abortController.signal,
          headers,
          body: JSON.stringify({ requests }),
        });

        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();

        // Use type-filtered results if available, otherwise fall back
        const typedHits = (data.results?.[0]?.hits || []).slice(0, maxItems);
        const fallbackHits = productType
          ? (data.results?.[1]?.hits || []).slice(0, maxItems)
          : [];

        hits = typedHits.length > 0 ? typedHits : fallbackHits;
        cache?.set(cacheKey, hits);
      }

      if (hits.length === 0) {
        this.#hide('no-recommendations');
        return;
      }

      const productsContainer = this.querySelector('[data-fbt-products]');
      if (!productsContainer) {
        this.#hide('no-container');
        return;
      }

      hits.forEach((hit) => {
        const varId = hit.objectID;
        const priceCents = Math.round((hit.price || 0) * 100);
        const compareCents = Math.round((hit.compare_at_price || 0) * 100);
        const productUrl = `/products/${hit.handle}?variant=${varId}`;
        const imgSrc = hit.image || '';

        const plus = document.createElement('span');
        plus.className = 'fbt__plus';
        plus.setAttribute('aria-hidden', 'true');
        plus.textContent = '+';

        const item = document.createElement('div');
        item.className = 'fbt__item';
        item.setAttribute('data-fbt-item', '');
        item.setAttribute('data-variant-id', varId);
        item.setAttribute('data-price', priceCents);

        const comparePriceHtml = compareCents > priceCents
          ? `<s class="fbt__compare-price">${this.#formatMoney(compareCents)}</s>`
          : '';

        item.innerHTML = `
          <label class="fbt__label">
            <input type="checkbox" class="fbt__checkbox" checked data-fbt-checkbox value="${varId}">
            <span class="fbt__checkbox-custom"></span>
            <a href="${productUrl}" class="fbt__card" target="_blank" rel="noopener">
              <div class="fbt__image-wrap">
                ${imgSrc ? `<img src="${imgSrc}" alt="${(hit.title || '').replace(/"/g, '&quot;')}" width="150" height="150" loading="lazy">` : ''}
              </div>
              <div class="fbt__info">
                <p class="fbt__title">${hit.title || ''}</p>
                <p class="fbt__price icon">
                  ${comparePriceHtml}
                  <span>${this.#formatMoney(priceCents)}</span>
                </p>
              </div>
            </a>
          </label>`;

        productsContainer.appendChild(plus);
        productsContainer.appendChild(item);
      });

      this.setAttribute('data-loaded', '');
      this.#bindEvents();
      this.#updateTotal();
    } catch (e) {
      if (e.name === 'AbortError') return;
      console.error('FBT error:', e.message);
      this.#hide('error');
    }
  }

  async #resolveObjectID(appId, apiKey, indexName, productId, fallbackVariantId, headers) {
    try {
      const res = await fetch(`https://${appId}-dsn.algolia.net/1/indexes/${indexName}/query`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          query: '',
          tagFilters: [`product:${productId}`],
          hitsPerPage: 1,
          distinct: true,
          attributesToRetrieve: ['objectID'],
        }),
      });
      if (!res.ok) return fallbackVariantId;
      const data = await res.json();
      return data.hits?.[0]?.objectID || fallbackVariantId;
    } catch {
      return fallbackVariantId;
    }
  }

  #bindEvents() {
    const checkboxes = this.querySelectorAll('[data-fbt-checkbox]');
    const addAllBtn = this.querySelector('[data-fbt-add-all]');

    checkboxes.forEach((cb) => {
      cb.addEventListener('change', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.#updateTotal();
      });
      cb.addEventListener('click', (e) => e.stopPropagation());
    });

    addAllBtn?.addEventListener('click', () => this.#addSelectedToCart());
  }

  #updateTotal() {
    const items = this.querySelectorAll('[data-fbt-item]');
    let total = 0;

    items.forEach((item) => {
      const cb = item.querySelector('[data-fbt-checkbox]');
      if (cb?.checked) {
        total += parseInt(item.dataset.price, 10) || 0;
      }
    });

    const totalEl = this.querySelector('[data-fbt-total]');
    if (totalEl) totalEl.textContent = this.#formatMoney(total);

    const addBtn = this.querySelector('[data-fbt-add-all]');
    const anyChecked = this.querySelector('[data-fbt-checkbox]:checked');
    if (addBtn) addBtn.disabled = !anyChecked;
  }

  async #addSelectedToCart() {
    const addBtn = this.querySelector('[data-fbt-add-all]');
    if (!addBtn || addBtn.disabled) return;

    const items = [];
    this.querySelectorAll('[data-fbt-item]').forEach((item) => {
      const cb = item.querySelector('[data-fbt-checkbox]');
      if (cb?.checked) {
        items.push({ id: parseInt(cb.value, 10), quantity: 1 });
      }
    });

    if (items.length === 0) return;

    addBtn.disabled = true;
    const originalText = addBtn.textContent;
    addBtn.textContent = addBtn.dataset.addingText || 'Adding…';

    const sectionIds = this.#getCartSectionIds();

    try {
      const body = { items };
      if (sectionIds.length > 0) {
        body.sections = sectionIds.join(',');
      }

      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || 'Could not add to cart');
      }

      const response = await res.json();

      addBtn.textContent = addBtn.dataset.addedText || 'Added ✓';

      const cartRes = await fetch('/cart.js');
      const cart = await cartRes.json();
      this.#refreshCart(response.sections, cart.item_count);

      setTimeout(() => {
        addBtn.textContent = originalText;
        addBtn.disabled = false;
      }, 2000);
    } catch (e) {
      console.error('FBT add-to-cart error:', e.message);
      addBtn.textContent = originalText;
      addBtn.disabled = false;
    }
  }

  #getCartSectionIds() {
    const ids = new Set();
    document.querySelectorAll('cart-items-component').forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) {
        ids.add(el.dataset.sectionId);
      }
    });
    return [...ids];
  }

  #refreshCart(sections, itemCount) {
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          data: {
            source: 'frequently-bought-together',
            itemCount: itemCount || 0,
            sections: sections || {},
          },
        },
      })
    );
  }

  #formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    const amountNoDecimals = Math.round(cents / 100).toString();
    const amountWithComma = amount.replace('.', ',');
    const amountNoDecimalsWithComma = amountNoDecimals.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    const moneyFormat = this.dataset.moneyFormat
      || window.Shopify?.currency?.moneyFormat
      || window.Theme?.moneyFormat
      || '{{amount}} SAR';

    return moneyFormat
      .replace('{{amount_with_comma_separator}}', amountWithComma)
      .replace('{{amount_no_decimals_with_comma_separator}}', amountNoDecimalsWithComma)
      .replace('{{amount_no_decimals}}', amountNoDecimals)
      .replace('{{amount}}', amount);
  }

  #hide(reason = '') {
    this.style.display = 'none';
    this.setAttribute('data-fbt-hidden', reason || 'unknown');
    if (reason && typeof console !== 'undefined' && console.warn) {
      const msg = {
        'no-objectid': 'Frequently Bought Together: Product not found in Algolia index (objectID). Ensure the product is synced and indexed with tag product:<id>.',
        'no-recommendations': 'Frequently Bought Together: Algolia returned 0 "bought together" recommendations for this product. The model needs enough co-purchase data; use Related Products in the meantime or send more conversion events to Algolia.',
        'no-container': 'Frequently Bought Together: Section DOM structure missing.',
        'error': 'Frequently Bought Together: Request failed (see console for details).',
      }[reason];
      if (msg) console.warn(msg);
    }
  }
}

if (!customElements.get('frequently-bought-together')) {
  customElements.define('frequently-bought-together', FrequentlyBoughtTogether);
}
